import { AxiosError } from 'axios';

const IndigoApiErrorType = 'IndigoApiError';

export default class IndigoApiError extends Error {
    public data?: unknown;
    public status?: number;
    public type = IndigoApiErrorType;

    public static async runHandlingErrorsAsync<K>(
        description: string,
        block: () => Promise<K>
    ): Promise<K> {
        try {
            return await block();
        } catch (error) {
            throw new IndigoApiError(description, error);
        }
    }

    public constructor(message: string, error?: unknown) {
        super(deriveMessage(message, error));

        this.data = error;

        if (isAxiosError(error)) {
            this.data = error.response?.data;
            this.status = error.response?.status;
        }
    }
}

function deriveMessage(message: string, error?: unknown) {
    const errorMessage = extractMessageFromError(error);
    if (errorMessage) {
        return `${message} failed: ${errorMessage}`;
    }
    return `[${message}] failed]`;
}

function extractMessageFromError(error?: unknown): string {
    const defaultMessage = 'API Error';

    if (!error) {
        return defaultMessage;
    }

    if (typeof error === 'string') {
        return error;
    }

    if (isAxiosError(error)) {
        if (error.response?.data?.message) {
            // If the back end has provided a message in its response data, then that wins...
            return error.response?.data?.message;
        }
        if (error.message) {
            return error.message;
        }
    }

    if (isErrorWithMessage(error)) {
        return error.message;
    }

    return 'API Error';
}

export function isIndigoApiError(error: unknown): error is IndigoApiError {
    return (error as IndigoApiError)?.type === IndigoApiErrorType;
}

export function isAxiosError(error: unknown): error is AxiosError {
    return !!(!isIndigoApiError(error) && (error as AxiosError)?.response);
}

export function isErrorWithMessage(
    error: unknown
): error is { message: string } {
    return !!(
        !isIndigoApiError(error) && (error as { message: string })?.message
    );
}
